import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ExternalLink, List } from 'lucide-react';

const CONFETTI = [
  { color: '#2563EB', tx: -60, ty: -50 },
  { color: '#F59E0B', tx: 70, ty: -40 },
  { color: '#22C55E', tx: -50, ty: 50 },
  { color: '#EF4444', tx: 60, ty: 40 },
  { color: '#A855F7', tx: 0, ty: -70 },
  { color: '#06B6D4', tx: -70, ty: 10 },
  { color: '#EAB308', tx: 70, ty: 15 },
  { color: '#EC4899', tx: -20, ty: 70 },
];

export default function Success() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/articles/${articleId}`);
        setArticle(data);
      } catch (e) { /* noop */ }
    })();
  }, [articleId]);

  return (
    <div className="mobile-shell">
      <div className="flex flex-col items-center justify-center min-h-[90vh] px-8 text-center">
        <div className="relative mb-8">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              className="confetti-dot"
              style={{
                background: c.color,
                left: '50%', top: '50%',
                '--tx': `${c.tx}px`, '--ty': `${c.ty}px`,
                animationDelay: `${i * 40}ms`,
              }}
            />
          ))}
          <img
            src="/brand/autoradko-icon.png"
            alt="Auto Radko"
            className="check-bounce w-40 h-40 select-none drop-shadow-xl rounded-3xl"
            draggable={false}
            data-testid="success-logo"
          />
        </div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="success-title">
          {article?.forced_to_draft ? 'Sauvegardé en brouillon !' : 'Publié !'}
        </h1>
        <p className="text-slate-500 mt-2 max-w-xs">
          {article?.forced_to_draft
            ? 'WordPress a refusé la publication directe (droits insuffisants). L\'article est en brouillon sur votre site — il ne reste qu\'à cliquer « Publier » dans wp-admin.'
            : 'Votre article a été publié avec succès sur votre site WordPress.'}
        </p>

        {article?.inline_image_used && (
          <div
            className="mt-4 max-w-xs px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 text-left"
            data-testid="notice-inline-image"
          >
            <b>Image affichée en ligne</b> — WordPress a refusé l'upload dans la médiathèque, l'image est intégrée directement dans le corps de l'article (visible sur le site, mais pas dans votre médiathèque WP).
          </div>
        )}
        {article?.forced_to_draft && (
          <div
            className="mt-3 max-w-xs px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 text-left"
            data-testid="notice-forced-draft"
          >
            <b>Action requise</b> — Ouvrez le lien ci-dessous, vérifiez le contenu, et cliquez sur « Publier » dans WordPress pour rendre l'article visible.
          </div>
        )}

        <div className="w-full max-w-xs mt-10 space-y-3">
          {article?.published_url && (
            <a
              href={article.published_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-view-on-site"
              className="w-full bg-blue-600 text-white font-semibold py-3.5 rounded-xl hover:bg-blue-700 active:scale-95 transition flex items-center justify-center gap-2"
            >
              Voir sur le site <ExternalLink size={16} />
            </a>
          )}
          <button
            onClick={() => nav('/')}
            data-testid="btn-back-to-feed"
            className="w-full border-2 border-slate-200 text-slate-700 font-semibold py-3.5 rounded-xl hover:bg-slate-50 active:scale-95 transition flex items-center justify-center gap-2"
          >
            <List size={16} /> Retour au feed
          </button>
        </div>
      </div>
    </div>
  );
}
